import { useEffect, useMemo, useState } from 'react';
import { getUsersByCompanyId } from '../api/firebase/firestore/user/read';
import { registerStatus } from '../constants/enum';

/** 共有先候補などに使う企業メンバー（有効ユーザー）1件分 */
export interface CompanyUserOption {
  id: string; // uid
  name: string;
  groupIds: string[]; // 所属グループID（重複バッジ抑制などに使用 #2963）
}

/**
 * 企業の有効メンバー（register_status = finished）を取得する共通フック（#2963）。
 * 読取パターン設定（共有担当者の候補）と一覧（共有範囲バッジ）で同じ取得を共有し、
 * getUsersByCompanyId の二重ロードを避ける。
 *
 * @param companyId 対象企業ID。未指定/空なら空配列を返す。
 * @returns users（名前昇順）と、派生マップ（uid→名 / uid→group_ids）
 */
export const useCompanyUsers = (
  companyId?: string | null,
): {
  users: CompanyUserOption[];
  userNameMap: Record<string, string>;
  userGroupsMap: Record<string, string[]>;
} => {
  const [users, setUsers] = useState<CompanyUserOption[]>([]);

  useEffect(() => {
    if (!companyId) {
      setUsers([]);
      return;
    }
    void (async (): Promise<void> => {
      try {
        const raw = await getUsersByCompanyId(companyId);
        setUsers(
          raw
            .filter(u => u.register_status === registerStatus.finished)
            .map(u => ({
              id: u.uid as string,
              name: (u.name as string) ?? '',
              groupIds: (u.group_ids as string[]) ?? [],
            }))
            .sort((a, b) => a.name.localeCompare(b.name, 'ja')),
        );
      } catch {
        setUsers([]);
      }
    })();
  }, [companyId]);

  const userNameMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u.name])), [users]);
  const userGroupsMap = useMemo(
    () => Object.fromEntries(users.map(u => [u.id, u.groupIds])),
    [users],
  );

  return { users, userNameMap, userGroupsMap };
};
